import { ERROR_REPORT_LANGS, type ErrorReportLang } from "../model/types";

/**
 * Пределы контракта с site_server (services_fastapi/error_reports.py обрезает и проверяет так же). Длины
 * сервер считает в знаках Юникода, здесь — в знаках UTF-16 (их не меньше), так что обрезанное здесь
 * проходит и там.
 */
export const LIMITS = {
    pageUrl: 1000,
    route: 200,
    selectedText: 1000,
    messageMin: 3,
    message: 2000,
    suggestion: 2000,
    /** context — не больше 4 КБ в JSON (UTF-8), как его пишет json.dumps сервера (contextBytes ниже). */
    contextBytes: 4096,
    /** Админский список: поиск ?q= (больше — 422). */
    search: 200,
    /** Заметка администратора при смене статуса (больше — 422). */
    adminNote: 1000,
} as const;

/**
 * Не длиннее n знаков UTF-16 и без половины суррогатной пары на конце. Одинокую половину (эмодзи или редкий
 * иероглиф ровно на границе) site_server в JSON не запишет: сообщение не сохранилось бы (503), и человек
 * повторял бы «Отправить» впустую.
 */
export const cut = (s: string, n: number): string => {
    if (s.length <= n) return s;
    const last = s.charCodeAt(n - 1);
    return s.slice(0, last >= 0xd800 && last <= 0xdbff ? n - 1 : n);
};

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

/** Текст, как его чистит site_server (error_reports._text): \r\n → \n, без управляющих знаков, без пробелов по краям. */
export const reportText = (s: string): string => (s || "").replace(/\r\n?/g, "\n").replace(CONTROL, "").trim();

/** Длина, как её считает site_server, — в знаках Юникода: «😀😀» — два знака, а не четыре половины пар. */
export const charCount = (s: string): number => Array.from(s || "").length;

/** Язык страницы — первый сегмент адреса, как в App.tsx; не язык сайта — en. */
export const pageLangOf = (pathname: string): ErrorReportLang => {
    const seg = (pathname || "").split("/")[1] || "";
    return (ERROR_REPORT_LANGS as readonly string[]).includes(seg) ? (seg as ErrorReportLang) : "en";
};

/**
 * Параметры адреса, которые уходят в сообщение, — белый список: только то, что нужно администратору, чтобы
 * открыть ту же страницу (вкладка, экран, поиск, время в видео, неделя, id записи). Остальное отбрасывается:
 * чёрный список по именам пропускал camelCase и чужие имена — коды входа (?code=, oobCode), токены
 * (consultationsEmailToken, redeem, invite, coupon, giftToken), сессии и счета оплаты (session_id, inv_id,
 * order_id), адреса почты, обратные адреса (from, return, url — внутри бывают те же токены). Администратору
 * нужна страница, а не чужой вход. Новый безопасный параметр страницы — дописать сюда.
 */
const SAFE_PARAMS: ReadonlySet<string> = new Set([
    // вкладки, экраны, разделы
    "tab", "s", "sheet", "section", "view", "open", "toc", "detail", "openRules", "sup", "supgo",
    // поиск, фильтры, сортировка
    "q", "supq", "sort", "kind", "type", "status", "scope", "sphere", "period", "operation", "lang", "language",
    "tgt", "limit", "groups", "marathon", "lecturer", "city", "country",
    // даты и время
    "d", "date", "week", "t", "time", "at", "m", "r",
    // id того, о чём страница
    "id", "act", "edit", "hash", "parent", "media", "clip", "video", "cue", "folder_num", "stream", "photo",
    // плеер и режимы страницы
    "captions", "quality", "speed", "volume", "embed", "partDone", "liveTest", "liveWorkout", "ar_preview",
    // подписи и предзаполнение форм
    "name", "title", "label", "via", "buy",
    // итог внешнего шага (оплата, вход): без кодов и сессий
    "payment_success", "error",
]);

/** Параметр адреса уходит в сообщение (белый список SAFE_PARAMS). */
export const isReportParam = (name: string): boolean => SAFE_PARAMS.has(name);

/** Адрес страницы для сообщения: без #, только безопасные параметры, не длиннее предела. */
export const reportPageUrl = (href: string): string => {
    try {
        const u = new URL(href);
        const kept = new URLSearchParams();
        u.searchParams.forEach((v, k) => {
            if (isReportParam(k)) kept.append(k, v);
        });
        const q = kept.toString();
        return `${u.origin}${u.pathname}${q ? `?${q}` : ""}`.slice(0, LIMITS.pageUrl);
    } catch {
        return String(href || "").split("#")[0].split("?")[0].slice(0, LIMITS.pageUrl);
    }
};

/** Пробелы и переводы строк выделения — одним пробелом; не длиннее предела. */
export const cleanSelection = (s: string): string => cut((s || "").replace(/\s+/g, " ").trim(), LIMITS.selectedText);

type SelectionRoot = { getSelection?: () => Selection | null };

/**
 * Выделенный текст страницы. Рецепты рисует остров в открытом shadow root: document.getSelection() видит
 * его выделение текстом, а где не видит — спрашиваем сам корень тени активного элемента (Chrome).
 */
export const currentSelection = (): string => {
    try {
        const s = cleanSelection(String(document.getSelection?.() || ""));
        if (s) return s;
        let el: Element | null = document.activeElement;
        for (let depth = 0; el && el.shadowRoot && depth < 4; depth++) {
            const root = el.shadowRoot as unknown as SelectionRoot & ShadowRoot;
            const t = cleanSelection(String(root.getSelection?.() || ""));
            if (t) return t;
            el = root.activeElement;
        }
    } catch {
        /* выделения нет — сообщение без цитаты */
    }
    return "";
};

/** Поле ввода: там Ctrl+Enter — своя отправка (ответы, комментарии), не наше сообщение. */
export const isEditable = (el: EventTarget | null): boolean => {
    const e = el as HTMLElement | null;
    if (!e || typeof e.tagName !== "string") return false;
    const tag = e.tagName.toLowerCase();
    return tag === "input" || tag === "textarea" || tag === "select" || !!e.isContentEditable;
};

/**
 * JSON, как его пишет site_server (json.dumps(ensure_ascii=False)): с пробелом после «,» и «:». Он на 2 байта
 * на поле длиннее JSON.stringify — мерь мы компактный, context у самой границы 4 КБ сервер отклонял бы
 * (422 context_too_large).
 */
export const serverJson = (v: unknown): string => {
    if (v !== null && typeof v === "object" && typeof (v as { toJSON?: unknown }).toJSON === "function") {
        return serverJson((v as { toJSON: () => unknown }).toJSON());
    }
    if (Array.isArray(v)) return `[${v.map((x) => serverJson(x === undefined || typeof x === "function" ? null : x)).join(", ")}]`;
    if (v !== null && typeof v === "object") {
        const fields = Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined && typeof x !== "function");
        return `{${fields.map(([k, x]) => `${JSON.stringify(k)}: ${serverJson(x)}`).join(", ")}}`;
    }
    return JSON.stringify(v) ?? "null";
};

/** Размер context, как его меряет site_server: байты UTF-8 его json.dumps. */
export const contextBytes = (v: unknown): number => new TextEncoder().encode(serverJson(v)).length;

/**
 * context не больше 4 КБ в JSON (меряется, как на сервере, — contextBytes): сначала укорачиваются длинные
 * строки (русская цитата шага бывает длинной), потом отбрасываются поля с конца. Числа и короткие поля
 * (id блюда, часть) остаются.
 */
export const fitContext = (ctx: Record<string, unknown> | undefined, max: number = LIMITS.contextBytes): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(ctx || {})) {
        if (v === undefined || typeof v === "function") continue;
        out[k] = v;
    }
    if (contextBytes(out) <= max) return out;
    for (const cap of [600, 300, 120]) {
        for (const [k, v] of Object.entries(out)) {
            if (typeof v === "string" && v.length > cap) out[k] = `${cut(v, cap)}…`;
        }
        if (contextBytes(out) <= max) return out;
    }
    const keys = Object.keys(out);
    while (keys.length && contextBytes(out) > max) delete out[keys.pop() as string];
    return out;
};

/**
 * Где кнопки «Нашли ошибку?» нет: служебная страница записи эфира, разговор и проверка устройства
 * (полноэкранные), консоль эфира, плеер (у субтитров своя форма правки), сама страница сообщений.
 */
const HIDDEN_ROUTE = /^\/(?:live-egress-layout(?:\/|$)|[a-z]{2}\/(?:call|devicecheck|player)(?:\/|$)|[a-z]{2}\/admin\/(?:stream|error-reports)(?:\/|$))/;

export const reportHiddenOn = (pathname: string): boolean => HIDDEN_ROUTE.test(pathname || "/");

/**
 * Внизу страницы стоит поле ввода (вопросы, общение, чат специалиста) — кнопка поднимается над ним,
 * как синяя кнопка поддержки (FloatingSupportButton).
 */
export const reportLiftedOn = (pathname: string): boolean =>
    /\/(questions|account\/questions)\/?$/.test(pathname) ||
    /\/account\/community(\/|$)/.test(pathname) ||
    /\/account\/specialist-chat(\/|$)/.test(pathname);

/** Кабинет на компьютере: слева боковая панель (--navbar-width) — кнопка правее неё. */
export const reportInCabinet = (pathname: string): boolean => /^\/[a-z]{2}\/account(\/|$)/.test(pathname);

/** Страница во фрейме (встроена в чужую страницу) — кнопки нет. */
export const inFrame = (): boolean => {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};
